<template>
  <div v-if="!shopTheLookLoading && shopTheLookProducts.length" class="mx-5">
    <div
      :class="{
        'my-10': !showReducedRecosSpacing,
        'mb-5 mt-10': showReducedRecosSpacing,
      }"
    >
      <span class="text-xl font-normal">
        {{ $t('recommendations.title.shop_the_look') }}
      </span>
    </div>
    <BasicSlider
      :items="shopTheLookProducts"
      key-prop="id"
      :pixel-gap="0"
      :center-content="false"
      @slide-visible="onSlideVisible"
    >
      <template #item="{ item: product, index }">
        <ProductCard
          :id="product.id"
          :key="`shop-the-look-recommendation-${product.id}`"
          :product="product"
          :siblings="getSiblingsColors(product) as ProductSibling[]"
          :colors="getProductAndSiblingsColors(product, 'colorRange')"
          :name="getName(product)"
          :link="toLocalePath(getProductDetailRoute(product))"
          :image="getProductImage(product.images, imageAssetType)"
          :price="lowestPrice(product)"
          :disable-hover-image="true"
          :show-add-to-cart="false"
          :show-add-to-wishlist="false"
          :show-available-colors="false"
          :hide-more-colors-label="true"
          :show-price-reduction="false"
          highlight-price-red
          class="px-1"
          @click:product="handleProductClick(product, index)"
        />
      </template>
    </BasicSlider>
  </div>
</template>

<script setup lang="ts">
import {
  getFirstAttributeValue,
  getProductAndSiblingsColors,
  type Product,
  type ProductSibling,
} from '@scayle/storefront-nuxt'
import ProductCard from '~/components/product/productCard/ProductCard.vue'
import BasicSlider from '~/components/ui/basicSlider/BasicSlider.vue'
import { useShopTheLookProducts } from '~/composables/useShopTheLookProducts'
import useCarouselListTracking from '#layers/base/composables/useCarouselListTracking'
import { SHOP_THE_LOOK_LIST_CONTEXT } from '#layers/base/constants/recommendations'
import useExperiments from '~/composables/useExperiments'

const props = defineProps<{ productId?: number }>()

const emit = defineEmits(['click:recommendation'])

const { showReducedRecosSpacing } = useExperiments()

const { shopTheLookProducts, isLoading: shopTheLookLoading } =
  useShopTheLookProducts(() => props.productId)

const imageAssetType = '_f'

const { onSlideVisible, onSelect } = useCarouselListTracking({
  context: SHOP_THE_LOOK_LIST_CONTEXT,
  total: () => shopTheLookProducts.value.length,
})

const handleProductClick = (product: Product, index: number) => {
  onSelect(product, index)
  emit('click:recommendation', product, index)
}

const { toLocalePath } = useRouteHelpers()

const getName = (product: Product) =>
  getFirstAttributeValue(product.attributes, 'name')?.label || ''

const lowestPrice = (product: Product) => getLowestPrice(product.variants || [])
</script>
